import React from "react";

import { FontAwesomeIcon }
from "@fortawesome/react-fontawesome";

import {
    faBolt,
    faChartLine,
    faClock,
    faCoins,
    faLeaf,
    faLightbulb,
    faTriangleExclamation
}
from "@fortawesome/free-solid-svg-icons";


export default function InsightDetail({
    insight
})
{

    //--------------------------------------------------------
    // Formatting
    //--------------------------------------------------------

    function displayValue(value)
    {
        if(
            value === null
            ||
            value === undefined
            ||
            value === ""
        )
        {
            return "—";
        }

        return value;
    }


    function formatMoney(value)
    {
        if(
            value === null
            ||
            value === undefined
        )
        {
            return "—";
        }

        return new Intl.NumberFormat(
            "en-GB",
            {
                style: "currency",
                currency: "GBP",
                maximumFractionDigits: 0
            }
        ).format(value);
    }


    function severityColour(severity)
    {
        switch((severity || "").toUpperCase())
        {
            case "CRITICAL":
                return "#ef3f45";

            case "HIGH":
                return "#ff6b13";

            case "MEDIUM":
                return "#f2b321";

            case "LOW":
                return "#20a15a";

            default:
                return "#7d8796";
        }
    }


    //--------------------------------------------------------
    // Render
    //--------------------------------------------------------

    if(!insight)
    {
        return null;
    }


    return (
        <>
            <div style={detailHeaderStyle}>

                <div style={eyebrowStyle}>
                    INSIGHT DETAIL
                </div>

                <h1 style={titleStyle}>
                    {insight.title}
                </h1>

                <div style={badgeRowStyle}>

                    <span
                        style={{
                            ...severityBadgeStyle,

                            background:
                                severityColour(
                                    insight.severity
                                )
                        }}
                    >
                        {
                            insight.severity
                            ||
                            "UNKNOWN"
                        }
                    </span>

                    {
                        insight.subcategory
                        &&
                        <span style={categoryBadgeStyle}>
                            {insight.subcategory}
                        </span>
                    }

                </div>

            </div>


            <div style={detailBodyStyle}>

                <DetailBlock
                    icon={faLightbulb}
                    title="Observation"
                >
                    {
                        insight.description
                        ||
                        "No description available."
                    }
                </DetailBlock>


                <DetailBlock
                    icon={faChartLine}
                    title="Recommendation"
                >
                    {
                        insight.recommendation
                        ||
                        "No recommendation available."
                    }
                </DetailBlock>


                <div style={metricGridStyle}>

                    <Metric
                        label="Risk score"
                        value={
                            displayValue(
                                insight.riskScore
                            )
                        }
                    />

                    <Metric
                        label="Confidence"
                        value={
                            insight.confidence != null
                                ?
                                insight.confidence + "%"
                                :
                                "—"
                        }
                    />

                    <Metric
                        label="Urgency"
                        value={
                            displayValue(
                                insight.urgency
                            )
                        }
                    />

                    <Metric
                        label="Priority"
                        value={
                            displayValue(
                                insight.businessPriority
                            )
                        }
                    />

                </div>


                <DetailBlock
                    icon={faTriangleExclamation}
                    title="Potential impact"
                >

                    <DetailRow
                        icon={faCoins}
                        label="Impact cost"
                        value={
                            formatMoney(
                                insight.impactCost
                            )
                        }
                    />

                    <DetailRow
                        icon={faClock}
                        label="Duration"
                        value={
                            insight.impactDurationHours != null
                                ?
                                insight.impactDurationHours
                                +
                                " hours"
                                :
                                "—"
                        }
                    />

                    <DetailRow
                        icon={faBolt}
                        label="Disruption"
                        value={
                            displayValue(
                                insight.operationalDisruption
                            )
                        }
                    />

                    <DetailRow
                        icon={faLeaf}
                        label="Carbon impact"
                        value={
                            displayValue(
                                insight.carbonImpact
                            )
                        }
                    />

                </DetailBlock>


                {
                    (
                        insight.complianceStatus
                        ||
                        insight.regulation
                    )
                    &&
                    <DetailBlock
                        icon={faTriangleExclamation}
                        title="Compliance"
                    >

                        <DetailRow
                            label="Status"
                            value={
                                displayValue(
                                    insight.complianceStatus
                                )
                            }
                        />

                        <DetailRow
                            label="Regulation"
                            value={
                                displayValue(
                                    insight.regulation
                                )
                            }
                        />

                    </DetailBlock>
                }

            </div>
        </>
    );
}


//--------------------------------------------------------
// Detail block
//--------------------------------------------------------

function DetailBlock({
    icon,
    title,
    children
})
{
    return (
        <div style={detailBlockStyle}>

            <div style={blockHeadingStyle}>

                {
                    icon
                    &&
                    <FontAwesomeIcon
                        icon={icon}
                        style={blockIconStyle}
                    />
                }

                {title}

            </div>

            <div style={blockContentStyle}>
                {children}
            </div>

        </div>
    );
}


//--------------------------------------------------------
// Metric
//--------------------------------------------------------

function Metric({
    label,
    value
})
{
    return (
        <div style={metricStyle}>

            <div style={metricLabelStyle}>
                {label}
            </div>

            <div style={metricValueStyle}>
                {value}
            </div>

        </div>
    );
}


//--------------------------------------------------------
// Detail row
//--------------------------------------------------------

function DetailRow({
    icon,
    label,
    value
})
{
    return (
        <div style={detailRowStyle}>

            <div style={rowLabelStyle}>

                {
                    icon
                    &&
                    <FontAwesomeIcon
                        icon={icon}
                        style={rowIconStyle}
                    />
                }

                {label}

            </div>

            <div style={rowValueStyle}>
                {value}
            </div>

        </div>
    );
}


//--------------------------------------------------------
// Header styles
//--------------------------------------------------------

const detailHeaderStyle =
{
    padding: "24px 24px 22px 24px",

    color: "#ffffff",

    background:
        "linear-gradient(145deg, #292e35 0%, #171a1f 55%, #101215 100%)",

    borderBottom:
        "1px solid rgba(255,255,255,0.08)",

    boxShadow:
        "0 10px 24px rgba(20, 26, 34, 0.16)"
};


const eyebrowStyle =
{
    marginBottom: "8px",

    color: "#8fa0b8",

    fontSize: "10px",
    fontWeight: "800",
    letterSpacing: "1.7px"
};


const titleStyle =
{
    margin: "0 0 16px 0",

    color: "#ffffff",

    fontSize: "22px",
    lineHeight: "1.28",
    fontWeight: "750"
};


const badgeRowStyle =
{
    display: "flex",
    flexWrap: "wrap",
    gap: "7px"
};


const severityBadgeStyle =
{
    padding: "6px 10px",

    color: "#ffffff",

    borderRadius: "6px",

    fontSize: "10px",
    fontWeight: "800",
    letterSpacing: "0.7px"
};


const categoryBadgeStyle =
{
    padding: "6px 10px",

    color: "#d2dae5",

    background:
        "rgba(255,255,255,0.08)",

    border:
        "1px solid rgba(255,255,255,0.13)",

    borderRadius: "6px",

    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.4px"
};


//--------------------------------------------------------
// Body styles
//--------------------------------------------------------

const detailBodyStyle =
{
    flex: 1,

    padding: "22px 24px 35px 24px",

    overflowY: "auto",

    background: "#ffffff"
};


const detailBlockStyle =
{
    marginBottom: "24px"
};


const blockHeadingStyle =
{
    display: "flex",
    alignItems: "center",
    gap: "8px",

    marginBottom: "10px",

    color: "#292f38",

    fontSize: "12px",
    fontWeight: "750",
    letterSpacing: "0.2px"
};


const blockIconStyle =
{
    width: "14px",

    color: "#477df5"
};


const blockContentStyle =
{
    color: "#606b7a",

    fontSize: "12.5px",
    lineHeight: "1.65"
};


//--------------------------------------------------------
// Metric styles
//--------------------------------------------------------

const metricGridStyle =
{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "9px",

    marginBottom: "25px"
};


const metricStyle =
{
    minWidth: 0,

    padding: "13px",

    background:
        "linear-gradient(145deg, #f7f8fa 0%, #f0f2f5 100%)",

    border: "1px solid #e0e4e9",
    borderRadius: "9px"
};


const metricLabelStyle =
{
    marginBottom: "6px",

    overflow: "hidden",

    color: "#8490a0",

    fontSize: "9px",
    fontWeight: "800",
    letterSpacing: "0.8px",

    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap"
};


const metricValueStyle =
{
    overflow: "hidden",

    color: "#272d36",

    fontSize: "14px",
    fontWeight: "750",

    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
};


//--------------------------------------------------------
// Row styles
//--------------------------------------------------------

const detailRowStyle =
{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    gap: "12px",

    padding: "10px 0",

    borderBottom:
        "1px solid #e8ebef"
};


const rowLabelStyle =
{
    display: "flex",
    alignItems: "center",
    gap: "8px",

    color: "#737f8f",

    fontSize: "11.5px"
};


const rowIconStyle =
{
    width: "12px",

    color: "#8a98aa"
};


const rowValueStyle =
{
    color: "#29303a",

    fontSize: "11.5px",
    fontWeight: "700",

    textAlign: "right"
};